// src/components/clubs/ClubRow.tsx

import React from "react";
import { useNavigate } from "react-router-dom";
import { ClubModel } from "../../models/ClubModel";
import { useTranslation } from "react-i18next";
import { resolveClubName } from "../../services/matchService";
import { FaLock } from "react-icons/fa";
import "./ClubRow.css";

import { clubUrl } from "../../utils/canonicalUrl";
interface ClubRowProps {
  club: ClubModel;
  isSelected: boolean;
  onSelect: () => void;
  // Called on mobile after navigation so App.tsx can switch to the entries pane
  onMobileNavigate?: () => void;
}

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";

const resolveLogoUrl = (url: string | null): string | null => {
  if (!url) return null;
  return url.startsWith("http") ? url : `${API_BASE}${url}`;
};

// Flags are stored under flags/ path — they need cover to fill the circle;
// club logos need contain to avoid cropping
const isFlag = (url: string | null): boolean => {
  return !!url && url.includes("flags/");
};

const getHeatClass = (count: number): string => {
  if (count === 0)  return "heat-0";
  if (count < 3)    return "heat-1";
  if (count < 8)    return "heat-2";
  if (count < 20)   return "heat-3";
  if (count < 50)   return "heat-4";
  return "heat-5";
};

const formatCount = (count: number): string => {
  if (count >= 1_000_000) return (count / 1_000_000).toFixed(1).replace(".", ",") + "m";
  if (count >= 1_000)     return (count / 1_000).toFixed(1).replace(".", ",") + "b";
  return String(count);
};

export const ClubRow: React.FC<ClubRowProps> = ({ club, isSelected, onSelect, onMobileNavigate }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const displayName = resolveClubName(club);
  const logoSrc = resolveLogoUrl(club.logoUrl);
  const recentCount = club.recentEntryCount ?? 0;

  const handleClick = () => {
    onSelect();
    navigate(clubUrl(club.slug, club.id));
    // On mobile: signal App.tsx to switch from the list pane to the content pane
    onMobileNavigate?.();
  };

  return (
    <div
      className={`club-row ${isSelected ? "selected" : ""} ${club.isLocked ? "locked" : ""}`}
      onClick={handleClick}
    >
      {/* Avatar */}
      <div className="club-row__avatar">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={club.shortName}
            className={isFlag(club.logoUrl) ? "flag-img" : "logo-img"}
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
        ) : (
          club.shortName.slice(0, 2).toUpperCase()
        )}
      </div>

      {/* Name */}
      <div className="club-row__info">
        <div className="club-row__name">{displayName}</div>
      </div>

      {/* Right: entry count badge + lock icon (always rightmost) */}
      <div className="club-row__right">
        <span
          className={`club-row__entry-badge ${getHeatClass(recentCount)}`}
          title={t("club_row_recent_entries", "Son 24 saatte entry")}
        >
          {formatCount(recentCount)}
        </span>
        {club.isLocked && (
          <span className="row-lock-icon" title={t("topic_locked_tooltip")}>
            <FaLock size={10} />
          </span>
        )}
      </div>
    </div>
  );
};